import { Link } from "react-router-dom";

/**
 * P156. The pieces of the Invite Card screen that are genuinely the same on
 * both sides of the hierarchy.
 *
 * A worker Invite Card and an Employer Invite Card are different domains -
 * different tables, different endpoints, different recipients - but their
 * STATUS, USAGE and EXPIRY behave identically, because both were built from
 * the same contract (see EmployerInviteCardStatuses, which mirrors
 * InviteCardStatuses "exactly" by its own doc comment).
 *
 * Extracted here rather than copied so the two screens cannot drift: the
 * owner's rule is that the job side looks like the worker side, and two
 * private copies of this logic is how that stops being true a month from now.
 * Anything side-SPECIFIC (what the card targets, what the note says, which
 * pipeline runs afterwards) deliberately stays in each page.
 */

export const LINK_HIDDEN_FOR_ROLE_TEXT = "ลิงก์จริงแสดงเฉพาะ Agent เจ้าของสายงาน";

// The stored language code is free text (the backend never normalises it), so
// older cards carry "bn" or "Bengali" while cards made now carry "Bangla".
// They all mean the same language, and the app renames it to Bangla everywhere
// (the workers this is for are Bangladeshi and call it that - see the mobile
// interpreter's M40 rename). Displaying "bn" raw is exactly the kind of
// techy value that confuses an agent reading the list, so both legacy spellings
// are shown as "Bangla" here; any other code is shown as-is.
export function formatLanguage(code) {
  const value = String(code ?? "").trim();
  if (!value) {
    return null;
  }
  const lower = value.toLowerCase();
  if (lower === "bn" || lower.startsWith("beng") || lower.startsWith("bang")) {
    return "Bangla";
  }
  return value;
}

/** "BD / Bangla", "BD", "Bangla", or "-" - whichever parts are present. */
export function formatTarget(card) {
  const parts = [card.intendedCountryCode, formatLanguage(card.intendedLanguageCode)].filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
}

// "Revoked" is deliberately absent: a revoked card never appears in an
// Agent's operational list, so a filter pill for it would be dead UI.
export const STATUS_FILTERS = ["All", "Active", "Expired"];

export const STATUS_BADGE_VARIANT = {
  Active: "success",
  Draft: "neutral",
  Expired: "warning",
  Revoked: "danger",
};

export function formatDateTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

// maxUses is null for a genuinely unlimited card (the real API contract).
// `N >= null` would coerce null to 0 in JS and wrongly read as "used up" for
// any unlimited card with usedCount > 0, so the null case is always checked
// first and explicitly, never left to fall through the numeric comparison.
//
// maxUses <= 0 is treated as unlimited too. It is not a value the create form
// can produce (it validates a positive integer or blank), but demo/seed cards
// carried maxUses=0 with usedCount=250 - which, read as a real cap, made a
// heavily-used card display as "used up / 250 of 0". A cap of zero is not a
// meaningful limit, so it reads the same as no limit rather than as nonsense.
function hasNoLimit(maxUses) {
  return maxUses == null || maxUses <= 0;
}

export function usageNote(card) {
  const usedCount = card.usedCount ?? 0;
  if (hasNoLimit(card.maxUses)) {
    return usedCount > 0 ? `ใช้ไปแล้ว ${usedCount} ครั้ง (ไม่จำกัดจำนวน)` : "ไม่จำกัดจำนวนการใช้งาน";
  }
  if (usedCount <= 0) {
    return "ยังไม่ถูกใช้งาน";
  }
  if (usedCount >= card.maxUses) {
    return "ใช้ครบจำนวนแล้ว";
  }
  return `ใช้ไปแล้ว ${usedCount} ครั้ง`;
}

/** "N / ไม่จำกัด" for an unlimited card, "N / max" for a capped one. */
export function formatUsage(card) {
  return `${card.usedCount ?? 0} / ${hasNoLimit(card.maxUses) ? "ไม่จำกัด" : card.maxUses}`;
}

export function MetricCard({ label, value, note, reservedNote, variant, to }) {
  const variantClass = variant === "green" ? " wx-ic-metric--green" : variant === "amber" ? " wx-ic-metric--amber" : "";
  const content = (
    <>
      <span className="wx-ic-metric-label">{label}</span>
      {reservedNote ? (
        <span className="wx-ic-metric-note">{reservedNote}</span>
      ) : (
        <>
          <span className="wx-ic-metric-value">{value}</span>
          {note && <span className="wx-ic-metric-note">{note}</span>}
        </>
      )}
    </>
  );

  if (to && !reservedNote) {
    return (
      <Link to={to} className={`wx-ic-metric wx-ic-metric--link${variantClass}`}>
        {content}
      </Link>
    );
  }

  return <div className={`wx-ic-metric${reservedNote ? " wx-ic-metric--reserved" : ""}${variantClass}`}>{content}</div>;
}

export function PublicUrlCell({ card }) {
  if (card.status === "Revoked") {
    return <span className="wx-ic-cell-sub">ลิงก์ถูกเพิกถอนแล้ว ไม่แสดงซ้ำ</span>;
  }
  if (!card.canViewInviteLink) {
    return <span className="wx-ic-cell-sub">{LINK_HIDDEN_FOR_ROLE_TEXT}</span>;
  }
  if (!card.inviteUrl) {
    return <span className="wx-ic-cell-sub">-</span>;
  }
  return <span className="wx-ic-url-pill">{card.inviteUrl.replace(/^https?:\/\//, "")}</span>;
}

export function StatusCell({ card }) {
  const variant = STATUS_BADGE_VARIANT[card.status] ?? "neutral";
  return (
    <>
      <span className={`wx-badge wx-badge--${variant}`}>{card.status}</span>
      {card.status === "Revoked" && card.revokedAtUtc && (
        <span className="wx-ic-cell-sub">เพิกถอนเมื่อ {formatDateTh(card.revokedAtUtc)}</span>
      )}
    </>
  );
}

export function ExpiresCell({ card }) {
  const formatted = formatDateTh(card.expiresAtUtc);
  if (!formatted) {
    return <span className="wx-ic-cell-sub">ไม่มีวันหมดอายุ</span>;
  }
  return (
    <>
      <span className="wx-ic-cell-strong">{formatted}</span>
      <span className="wx-ic-cell-sub">ICT</span>
    </>
  );
}
